import SimpleBar from 'simplebar';

import { EventDispatcher } from '@sg-core/resources/assets/js/libs/event-dispatcher';
import { Transition, getFocusableElements, lazySelector } from '@sg-core/resources/assets/js/utils';

export interface CollapseOptions {
  recalculateSimpleBar?: boolean;
  minHeight?: string;
  replaceCollapse?: boolean;
  closeOnClickOutside?: boolean;
  beforeExpand?: () => void;
  afterExpand?: () => void;
  beforeCollapse?: () => void;
  afterCollapse?: () => void;
}

export const STATES = {
  EXPANDED: 'phxsg-is-expanded',
  TRANSITIONING: 'phxsg-is-transitioning',
  GROWING: 'phxsg-is-growing',
  SHRINKING: 'phxsg-is-shrinking',
  CALCULATING: 'phxsg-is-calculating',
} as const;

export const COLLAPSE_EVENTS = {
  EXPAND: 'expand',
  EXPANDED: 'expanded',
  COLLAPSE: 'collapse',
  COLLAPSED: 'collapsed',
} as const;

const dataStore = new WeakMap<HTMLElement, Map<string, Collapse>>();

function data(element: HTMLElement): {
  set(key: string, value: Collapse): void;
  get(key: string): Collapse | undefined;
} {
  if (!dataStore.has(element)) {
    dataStore.set(element, new Map());
  }

  const store = dataStore.get(element)!;

  return {
    set: (key: string, value: Collapse) => {
      store.set(key, value);
    },
    get: (key: string) => store.get(key),
  };
}

const classInstances = new WeakMap<HTMLElement, Collapse>();

/**
 * A helper for managing collapsible elements with accessibility support
 */
export class Collapse extends EventDispatcher {
  private element: HTMLElement;

  private triggerElement!: HTMLElement;

  private options: CollapseOptions;

  private group: string | null;

  private transition: Transition | null = null;

  private handleToggle = (e: Event) => this.toggle(e);

  private handleKeydown = (e: KeyboardEvent) => this._handleKeyboardNavigation(e);

  constructor(element: HTMLElement | string, options?: CollapseOptions) {
    super();

    this.element = lazySelector(element) as HTMLElement;
    classInstances.set(this.element, this);

    this.options = {
      recalculateSimpleBar: true,
      minHeight: '',
      replaceCollapse: false,
      closeOnClickOutside: false,
      ...options,
    };
    this.group = this.element.getAttribute('data-group');

    this._initTrigger();

    data(this.element).set('collapse', this);

    if (this.options.minHeight) {
      this.element.style.height = this.options.minHeight;
    }

    if (this.options.replaceCollapse) {
      window.addEventListener('resize', () => this._setPosition());
    }
  }

  get trigger(): HTMLElement {
    return this.triggerElement;
  }

  private _initTrigger(): void {
    this.triggerElement = document.querySelector(
      `[data-toggle="collapse"][data-target="#${this.element.id}"]`,
    ) as HTMLElement;

    this._addEventListeners();
  }

  private _setToggleExpanded(expanded: boolean): void {
    this.triggerElement.setAttribute('aria-expanded', expanded.toString());
  }

  private _setGroupState(): void {
    if (!this.group) {
      return;
    }

    const siblings = Array.from(
      document.querySelectorAll<HTMLElement>(`.phxsg-collapse[data-group="${this.group}"]`),
    ).filter((collapse) => collapse !== this.element);

    siblings.forEach((sibling) => {
      if (sibling.classList.contains(STATES.EXPANDED)) {
        const collapseInstance = data(sibling).get('collapse') as Collapse;
        collapseInstance.collapse();
      }
    });
  }

  private _setHeightAndExpand(): void {
    this.element.classList.add(STATES.CALCULATING);

    let width = this.element.parentElement!.getBoundingClientRect().width;
    const parentStyle = getComputedStyle(this.element.parentElement!);
    width -= parseFloat(parentStyle.paddingLeft) + parseFloat(parentStyle.paddingRight);

    this.element.style.width = `${width}px`;

    const height = this.element.getBoundingClientRect().height;

    this.element.style.width = '';

    this.element.classList.remove(STATES.CALCULATING);
    this.element.classList.add(STATES.TRANSITIONING);
    this.element.classList.add(STATES.GROWING);

    setTimeout(() => {
      this.element.style.height = `${height}px`;
    }, 50);
  }

  private _addEventListeners(): void {
    this.triggerElement.addEventListener('click', this.handleToggle);
    this.triggerElement.addEventListener('keydown', this.handleKeydown);
    this.element.addEventListener('keydown', this.handleKeydown);
  }

  private _removeEventListeners(): void {
    this.triggerElement.removeEventListener('click', this.handleToggle);
    this.triggerElement.removeEventListener('keydown', this.handleKeydown);
    this.element.removeEventListener('keydown', this.handleKeydown);
  }

  private _setPosition(): void {
    if (!this.options.replaceCollapse) return;

    const right = this.element.getBoundingClientRect().right;
    const width = this.element.getBoundingClientRect().width;
    const spacing = window.innerWidth - right;

    if (spacing < width) {
      this.element.style.right = '0px';
    } else {
      this.element.style.right = 'auto';
    }
  }

  private _handleClickOutside = (e: Event): void => {
    const inBody = (e.target as HTMLElement).closest('.phxsg-collapse__body');

    if (!inBody) {
      this.collapse();
    }
  };

  private _isTransitioning(): boolean {
    const transitioningSiblings = Array.from(
      document.querySelectorAll<HTMLElement>(`.phxsg-collapse[data-group="${this.group}"]`),
    ).filter((collapse) => collapse.classList.contains(STATES.TRANSITIONING));

    return this.element.classList.contains(STATES.TRANSITIONING) || transitioningSiblings.length > 0;
  }

  private _handleKeyboardNavigation = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.collapse();
      this.triggerElement.focus();
      return;
    }

    const focusable = getFocusableElements(this.element) as HTMLElement[];

    if (e.target === this.triggerElement) {
      if (e.key === ' ' || e.key === 'Enter') {
        this.expand();
      }

      if (focusable.length === 0 && e.key === 'Tab') {
        this.collapse();
      }

      if (focusable.length > 0 && e.key === 'Tab' && !e.shiftKey && this.element.classList.contains(STATES.EXPANDED)) {
        e.preventDefault();
        focusable[0].focus();
      }

      if (e.key === 'Tab' && e.shiftKey) {
        this.collapse();
      }
    }

    if (e.target === focusable[0] && e.key === 'Tab' && e.shiftKey) {
      this.collapse();
    }

    if (focusable.length > 0 && e.target === focusable[focusable.length - 1] && e.key === 'Tab' && !e.shiftKey) {
      this.collapse();
    }
  };

  private _recalculateSimpleBar(): void {
    if (!this.options.recalculateSimpleBar) return;

    const simplebarEl = this.element.closest('[data-simplebar]');
    if (!simplebarEl) return;

    const instance = SimpleBar.instances.get(simplebarEl as HTMLElement);
    if (instance) {
      instance.recalculate();
    }
  }

  public expand(immediate = false): void {
    if (this.element.classList.contains(STATES.EXPANDED) || this._isTransitioning()) {
      return;
    }

    this._setGroupState();

    if (immediate) {
      this.element.classList.add(STATES.EXPANDED);
    } else {
      this.transition = new Transition({
        element: this.element,
        fallback: true,
        property: 'height',
        before: () => {
          this.dispatchEvent(COLLAPSE_EVENTS.EXPAND, {
            trigger: this.triggerElement,
          });

          this.options.beforeExpand?.();
        },
        transition: () => {
          this._setHeightAndExpand();
        },
        after: () => {
          this.element.style.height = 'auto';
          this.element.classList.remove(STATES.TRANSITIONING);
          this.element.classList.remove(STATES.GROWING);
          this.element.classList.add(STATES.EXPANDED);
          this.options.afterExpand?.();

          this._recalculateSimpleBar();

          this.dispatchEvent(COLLAPSE_EVENTS.EXPANDED);
        },
      });
    }

    this._setToggleExpanded(true);

    if (this.options.closeOnClickOutside) {
      document.addEventListener('click', this._handleClickOutside);
    }
  }

  public collapse(immediate = false): void {
    if (!this.element.classList.contains(STATES.EXPANDED) || this._isTransitioning()) {
      return;
    }

    if (immediate) {
      this.element.classList.remove(STATES.EXPANDED);
    } else {
      this.transition = new Transition({
        element: this.element,
        fallback: true,
        property: 'height',
        before: () => {
          this.dispatchEvent(COLLAPSE_EVENTS.COLLAPSE, {
            trigger: this.triggerElement,
          });

          this.element.style.height = `${this.element.getBoundingClientRect().height}px`;

          this.options.beforeCollapse?.();
        },
        transition: () => {
          this.element.classList.remove(STATES.EXPANDED);
          this.element.classList.add(STATES.TRANSITIONING);
          this.element.classList.add(STATES.SHRINKING);
          setTimeout(() => {
            this.element.style.height = this.options.minHeight ?? '';
          }, 50);
        },
        after: () => {
          this.element.classList.remove(STATES.TRANSITIONING);
          this.element.classList.remove(STATES.SHRINKING);

          this.options.afterCollapse?.();

          this._recalculateSimpleBar();

          this.dispatchEvent(COLLAPSE_EVENTS.COLLAPSED);
        },
      });
    }

    this._setToggleExpanded(false);

    if (this.options.closeOnClickOutside) {
      document.removeEventListener('click', this._handleClickOutside);
    }
  }

  public toggle = (e: Event): void => {
    e.preventDefault();

    if (this._isTransitioning()) {
      return;
    }

    if (this.transition) {
      this.transition.destroy();
    }

    if (this.element.classList.contains(STATES.EXPANDED)) {
      this.collapse();
    } else {
      this.expand();
    }

    this._setPosition();
  };

  public destroy(): void {
    this._removeEventListeners();
    classInstances.delete(this.element);
  }
}
